import { useState } from 'react'
import {
  useCreateChecklistItem,
  useCreateChecklistSection,
  useDocumentsByChecklistItem,
  useFolders,
  useSetChecklistSectionFolder,
  useUpdateChecklistItem,
  useUpdateChecklistSection,
} from '../api/rooms'
import { ApiError } from '../api/client'
import type {
  ChecklistItemBody,
  ChecklistItemResponse,
  ChecklistPriority,
  ChecklistRequestKind,
  ChecklistSectionBody,
  ChecklistSectionResponse,
} from '../api/types'
import { checklistItemName } from '../lib/checklist'
import { errorMessage } from '../lib/format'

/** The API's message for one field, when it named one (422 and 409 carry `violations`). */
function fieldError(error: unknown, field: string): string | undefined {
  return error instanceof ApiError ? error.violations[field] : undefined
}

/**
 * An item of the request list, written by the owner (ADR 0007). The title is the short line
 * the list, the upload picker and the chips show; the request is the request, as long as it
 * needs to be, and its line structure is kept: a `<textarea>`, because the single-line input
 * this form used to have cannot hold "i) ... ii) ... iii) ...".
 */
export function ChecklistItemForm({
  roomId,
  sections,
  items = [],
  item,
  defaultSectionId,
  onDone,
}: {
  roomId: string
  sections: ChecklistSectionResponse[]
  /** The room's other requests, for "See also". */
  items?: ChecklistItemResponse[]
  item?: ChecklistItemResponse
  defaultSectionId?: string | null
  onDone: () => void
}) {
  const create = useCreateChecklistItem(roomId)
  const update = useUpdateChecklistItem(roomId)
  const [title, setTitle] = useState(item?.title ?? '')
  const [sectionId, setSectionId] = useState(item?.sectionId ?? defaultSectionId ?? '')
  const [category, setCategory] = useState(item?.category ?? '')
  const [priority, setPriority] = useState<ChecklistPriority | ''>(item?.priority ?? '')
  const [requestKind, setRequestKind] = useState<ChecklistRequestKind | ''>(item?.requestKind ?? '')
  const [requestText, setRequestText] = useState(item?.requestText ?? '')
  const [providerNotes, setProviderNotes] = useState(item?.providerNotes ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  // ADR 0009. This form shows all four, so it always sends all four: the update says includesTrackingFields,
  // under which a field left out would be cleared.
  const [requestedOn, setRequestedOn] = useState(item?.requestedOn ?? '')
  const [goalDate, setGoalDate] = useState(item?.goalDate ?? '')
  const [relatedItemId, setRelatedItemId] = useState(item?.relatedItem?.id ?? '')
  const [formDocumentId, setFormDocumentId] = useState(item?.formDocument?.id ?? '')
  // The form to fill in is chosen from the documents already answering this request: upload the blank here, then name it.
  const answering = useDocumentsByChecklistItem(roomId, item?.id ?? '', item !== undefined)
  const pending = create.isPending || update.isPending
  const failure = create.error ?? update.error

  const submit = () => {
    const body: ChecklistItemBody = {
      title: title.trim(),
      description: description.trim() || null,
      sectionId: sectionId || null,
      category: category.trim() || null,
      priority: priority || null,
      requestKind: requestKind || null,
      // Not trimmed inside: the request's lines and indents are content.
      requestText: requestText.trim() ? requestText : null,
      providerNotes: providerNotes.trim() ? providerNotes : null,
      requestedOn: requestedOn || null,
      goalDate: goalDate || null,
      relatedItemId: relatedItemId || null,
      formDocumentId: formDocumentId || null,
    }
    if (!body.title) return
    if (item) update.mutate({ itemId: item.id, ...body }, { onSuccess: onDone })
    else create.mutate(body, { onSuccess: onDone })
  }

  return (
    <form
      className="form form-wide"
      aria-label={item ? `Edit ${item.title}` : 'Add an item'}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="field">
        <label>
          Title
          <input
            type="text"
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Audited accounts 2025"
            autoFocus
          />
        </label>
        <p className="small muted no-margin">The short line the list and the tags show.</p>
        {fieldError(failure, 'title') && (
          <p className="field-error">{fieldError(failure, 'title')}</p>
        )}
      </div>
      <div className="field">
        <label>
          Request
          <textarea
            className="request-input"
            rows={6}
            maxLength={20000}
            value={requestText}
            onChange={(e) => setRequestText(e.target.value)}
            placeholder="The request in full. Line breaks are kept."
          />
        </label>
      </div>
      <div className="form-row">
        <div className="field">
          <label>
            Section
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              <option value="">No section</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          {fieldError(failure, 'sectionId') && (
            <p className="field-error">{fieldError(failure, 'sectionId')}</p>
          )}
        </div>
        <div className="field">
          <label>
            Category
            <input
              type="text"
              maxLength={100}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Corporate & Structure"
            />
          </label>
        </div>
        <div className="field">
          <label>
            Priority
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as ChecklistPriority | '')}
            >
              <option value="">None</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </label>
        </div>
        <div className="field">
          <label>
            Kind
            <select
              value={requestKind}
              onChange={(e) => setRequestKind(e.target.value as ChecklistRequestKind | '')}
            >
              <option value="">Not said</option>
              <option value="Static">Static</option>
              <option value="Dynamic">Dynamic</option>
            </select>
          </label>
        </div>
      </div>
      <div className="field">
        <label>
          Note to the responder
          <textarea
            className="request-input"
            rows={2}
            maxLength={8000}
            value={providerNotes}
            onChange={(e) => setProviderNotes(e.target.value)}
            placeholder="Which report to run, what would make this unnecessary"
          />
        </label>
      </div>
      <div className="form-row">
        <div className="field">
          <label>
            Requested on
            <input
              type="date"
              value={requestedOn}
              onChange={(e) => setRequestedOn(e.target.value)}
            />
          </label>
        </div>
        <div className="field">
          <label>
            Goal date
            <input type="date" value={goalDate} onChange={(e) => setGoalDate(e.target.value)} />
          </label>
          <span className="field-error">{fieldError(failure, 'goalDate')}</span>
        </div>
        <div className="field">
          <label>
            See also
            <select value={relatedItemId} onChange={(e) => setRelatedItemId(e.target.value)}>
              <option value="">Nothing</option>
              {items
                .filter((i) => i.id !== item?.id)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {checklistItemName(i)}
                  </option>
                ))}
            </select>
          </label>
          <span className="field-error">{fieldError(failure, 'relatedItemId')}</span>
        </div>
        {item && (
          <div className="field">
            <label>
              Form to fill in
              <select value={formDocumentId} onChange={(e) => setFormDocumentId(e.target.value)}>
                <option value="">None</option>
                {item.formDocument &&
                  !(answering.data ?? []).some((d) => d.id === item.formDocument?.id) && (
                    <option value={item.formDocument.id}>{item.formDocument.name}</option>
                  )}
                {(answering.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="small muted">
              One of the documents answering this request: upload the blank, then choose it here.
            </span>
            <span className="field-error">{fieldError(failure, 'formDocumentId')}</span>
          </div>
        )}
      </div>
      <div className="field">
        <label>
          Internal note
          <input
            type="text"
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </div>
      {item && item.sectionId !== (sectionId || null) && item.docLabel !== null && (
        <p className="small muted no-margin">
          Moving this item keeps its label, {item.docLabel}: correspondence that cites it stays
          right.
        </p>
      )}
      {failure && <p className="error-text small">{errorMessage(failure)}</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={pending || !title.trim()}>
          {item ? 'Save' : 'Add'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/** A section of the request list: the workbook's workstream. */
export function ChecklistSectionForm({
  roomId,
  section,
  onDone,
}: {
  roomId: string
  section?: ChecklistSectionResponse
  onDone: () => void
}) {
  const create = useCreateChecklistSection(roomId)
  const update = useUpdateChecklistSection(roomId)
  const setFolder = useSetChecklistSectionFolder(roomId)
  const folders = useFolders(roomId)
  const [folderId, setFolderId] = useState(section?.vdrFolderId ?? '')
  const [code, setCode] = useState(section?.code ?? '')
  const [name, setName] = useState(section?.name ?? '')
  const [serviceProvider, setServiceProvider] = useState(section?.serviceProvider ?? '')
  const [vdrIndexHint, setVdrIndexHint] = useState(section?.vdrIndexHint ?? '')
  const [description, setDescription] = useState(section?.description ?? '')
  const pending = create.isPending || update.isPending || setFolder.isPending
  const failure = create.error ?? update.error ?? setFolder.error
  const codeOk = /^[A-Za-z0-9]{1,10}$/.test(code.trim())

  const submit = () => {
    const body: ChecklistSectionBody = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      description: description.trim() || null,
      vdrIndexHint: vdrIndexHint.trim() || null,
      serviceProvider: serviceProvider.trim() || null,
    }
    if (!body.name || !codeOk) return
    if (!section) {
      create.mutate(body, { onSuccess: onDone })
      return
    }
    // The folder link is its own route (it is a pointer, not a field of the section), sent only when it changed.
    const folderChanged = (section.vdrFolderId ?? '') !== folderId
    update.mutate(
      { sectionId: section.id, ...body },
      {
        onSuccess: () =>
          folderChanged
            ? setFolder.mutate(
                { sectionId: section.id, folderId: folderId || null },
                { onSuccess: onDone },
              )
            : onDone(),
      },
    )
  }

  return (
    <form
      className="form form-wide"
      aria-label={section ? `Edit section ${section.name}` : 'Add a section'}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="form-row">
        <div className="field">
          <label>
            Code
            <input
              type="text"
              maxLength={10}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="LEG"
              autoFocus
            />
          </label>
          {fieldError(failure, 'code') && (
            <p className="field-error">{fieldError(failure, 'code')}</p>
          )}
        </div>
        <div className="field">
          <label>
            Name
            <input
              type="text"
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Legal"
            />
          </label>
          {fieldError(failure, 'name') && (
            <p className="field-error">{fieldError(failure, 'name')}</p>
          )}
        </div>
        <div className="field">
          <label>
            Provider
            <input
              type="text"
              maxLength={200}
              value={serviceProvider}
              onChange={(e) => setServiceProvider(e.target.value)}
              placeholder="The firm that owns this workstream"
            />
          </label>
        </div>
        <div className="field">
          <label>
            Folder index
            <input
              type="text"
              maxLength={20}
              value={vdrIndexHint}
              onChange={(e) => setVdrIndexHint(e.target.value)}
              placeholder="1.2"
            />
          </label>
        </div>
        {section && (
          <div className="field">
            <label>
              Linked folder
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                <option value="">None</option>
                {(folders.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.indexNumber} {f.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="small muted">
              Where this workstream's answers go. Its live index replaces the hint, and uploads made
              from a request land here.
            </span>
          </div>
        )}
      </div>
      <div className="field">
        <label>
          Description
          <input
            type="text"
            maxLength={1000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </div>
      {failure && <p className="error-text small">{errorMessage(failure)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={pending || !name.trim() || !codeOk}
        >
          {section ? 'Save' : 'Add'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
